import { useEffect, useState } from "react";
import {
  Plus,
  PencilSimple,
  Trash,
  CaretUp,
  CaretDown,
  Funnel,
  ArrowsClockwise,
  Calculator,
  ClockCounterClockwise,
  CheckCircle,
  XCircle,
  WarningCircle,
} from "@phosphor-icons/react";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, formatPrice } from "@/lib/api";
import { toast } from "sonner";

const RULE_TYPES = [
  { v: "markup", l: "Markup (increase)" },
  { v: "markdown", l: "Markdown (discount)" },
];
const METHODS = [
  { v: "fixed_per_pax", l: "Fixed per passenger" },
  { v: "percentage", l: "Percentage" },
  { v: "fixed_per_booking", l: "Fixed per booking" },
];
const TRIP_DIRECTIONS = [
  { v: "any", l: "Any" },
  { v: "one_way", l: "One-way" },
  { v: "round_trip", l: "Round-trip" },
  { v: "multi_city", l: "Multi-city" },
];
const COMBINE_MODES = [
  { v: "combine", l: "Combine with other rules" },
  { v: "stop_after", l: "Stop further rules after this" },
  { v: "solo", l: "Cannot combine (solo)" },
];
const CABINS = ["economy", "premium", "business", "first"];
const PASSENGER_TYPES = ["ADULT", "CHILD", "INFANT", "SENIOR"];

const PRODUCT_TYPES = [
  { v: "flights", l: "Flights" },
  { v: "hotels", l: "Hotels (future)" },
  { v: "cars", l: "Car rentals (future)" },
  { v: "packages", l: "Packages (future)" },
  { v: "insurance", l: "Insurance (future)" },
];

const BLANK_RULE = {
  name: "",
  rule_type: "markup",
  method: "percentage",
  amount: 0,
  product_type: "flights",
  origin_airports: [], origin_cities: [], origin_countries: [],
  destination_airports: [], destination_cities: [], destination_countries: [],
  trip_direction: "any",
  airlines: [], cabins: [], passenger_types: [],
  booking_date_start: "", booking_date_end: "",
  travel_date_start: "", travel_date_end: "",
  active: true,
  priority: 100,
  combine_mode: "combine",
  min_amount: "", max_amount: "", max_percent: "",
  allow_below_cost: false,
};

function CSVInput({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="tcp-overline text-[#666460] block mb-1.5">{label}</label>
      <input
        type="text"
        value={(value || []).join(", ")}
        onChange={(e) => onChange(e.target.value.split(/[\s,]+/).filter(Boolean))}
        placeholder={placeholder}
        data-testid={testid}
        className="tcp-input"
      />
    </div>
  );
}

function ChipsInput({ label, options, value, onChange, testid }) {
  const toggle = (v) =>
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  return (
    <div>
      <label className="tcp-overline text-[#666460] block mb-1.5">{label}</label>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <button
            key={o}
            type="button"
            onClick={() => toggle(o)}
            data-testid={`${testid}-${o}`}
            className={`px-3 py-1.5 text-xs tracking-wider uppercase border transition-colors ${
              value.includes(o)
                ? "bg-[#C29B62] text-[#0A0A0B] border-[#C29B62]"
                : "border-[#E0DDD6] text-[#666460] hover:border-[#C29B62] hover:text-[#C29B62]"
            }`}
          >
            {o}
          </button>
        ))}
      </div>
    </div>
  );
}

function RuleEditor({ open, initial, onClose, onSaved }) {
  const [draft, setDraft] = useState(initial || BLANK_RULE);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(initial || BLANK_RULE);
  }, [initial, open]);

  const update = (patch) => setDraft((d) => ({ ...d, ...patch }));

  const save = async () => {
    if (!draft.name.trim()) {
      toast.error("Rule name is required");
      return;
    }
    if (!draft.method) {
      toast.error("Calculation method is required");
      return;
    }
    const body = {
      ...draft,
      amount: Number(draft.amount) || 0,
      priority: Number(draft.priority) || 100,
      min_amount: draft.min_amount === "" ? null : Number(draft.min_amount),
      max_amount: draft.max_amount === "" ? null : Number(draft.max_amount),
      max_percent: draft.max_percent === "" ? null : Number(draft.max_percent),
      booking_date_start: draft.booking_date_start || null,
      booking_date_end: draft.booking_date_end || null,
      travel_date_start: draft.travel_date_start || null,
      travel_date_end: draft.travel_date_end || null,
    };
    delete body.id;
    delete body.created_by;
    delete body.created_at;
    delete body.updated_by;
    delete body.updated_at;
    setSaving(true);
    try {
      if (initial?.id) {
        await api.put(`/admin/pricing/rules/${initial.id}`, body);
        toast.success("Rule updated");
      } else {
        await api.post("/admin/pricing/rules", body);
        toast.success("Rule created");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save rule");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto bg-[#F7F5F0] border-[#E0DDD6]"
        data-testid="rule-editor"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl tracking-tighter text-[#1C1C1C]">
            {initial?.id ? "Edit pricing rule" : "Create pricing rule"}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-4">
          <div className="md:col-span-2">
            <label className="tcp-overline text-[#666460] block mb-1.5">Rule name *</label>
            <input
              value={draft.name}
              onChange={(e) => update({ name: e.target.value })}
              data-testid="rule-name"
              placeholder='e.g. "JFK → London business markup"'
              className="tcp-input"
            />
          </div>

          <div>
            <label className="tcp-overline text-[#666460] block mb-1.5">Direction</label>
            <select
              value={draft.rule_type}
              onChange={(e) => update({ rule_type: e.target.value })}
              data-testid="rule-type"
              className="tcp-input"
            >
              {RULE_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
          </div>

          <div>
            <label className="tcp-overline text-[#666460] block mb-1.5">Method *</label>
            <select
              value={draft.method}
              onChange={(e) => update({ method: e.target.value })}
              data-testid="rule-method"
              className="tcp-input"
            >
              {METHODS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
            </select>
          </div>

          <div>
            <label className="tcp-overline text-[#666460] block mb-1.5">
              Amount {draft.method === "percentage" ? "(%)" : "(USD)"}
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={draft.amount}
              onChange={(e) => update({ amount: e.target.value })}
              data-testid="rule-amount"
              className="tcp-input"
            />
          </div>

          <div>
            <label className="tcp-overline text-[#666460] block mb-1.5">Priority (lower = first)</label>
            <input
              type="number"
              min="0"
              value={draft.priority}
              onChange={(e) => update({ priority: e.target.value })}
              data-testid="rule-priority"
              className="tcp-input"
            />
          </div>

          <div>
            <label className="tcp-overline text-[#666460] block mb-1.5">Product type</label>
            <select
              value={draft.product_type}
              onChange={(e) => update({ product_type: e.target.value })}
              className="tcp-input"
            >
              {PRODUCT_TYPES.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
            </select>
          </div>

          <div>
            <label className="tcp-overline text-[#666460] block mb-1.5">Combine mode</label>
            <select
              value={draft.combine_mode}
              onChange={(e) => update({ combine_mode: e.target.value })}
              data-testid="rule-combine-mode"
              className="tcp-input"
            >
              {COMBINE_MODES.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
            </select>
          </div>

          {/* --- Matchers --- */}
          <div className="md:col-span-2 mt-3">
            <h4 className="font-serif text-xl text-[#1C1C1C] mb-3 pb-1 border-b border-[#E0DDD6]">Where this rule applies</h4>
          </div>

          <CSVInput
            label="Origin airport codes (IATA)"
            value={draft.origin_airports}
            onChange={(v) => update({ origin_airports: v })}
            placeholder="JFK, LGA, EWR"
            testid="rule-origin-airports"
          />
          <CSVInput
            label="Origin cities"
            value={draft.origin_cities}
            onChange={(v) => update({ origin_cities: v })}
            placeholder="New York"
          />
          <CSVInput
            label="Origin country codes (ISO-2)"
            value={draft.origin_countries}
            onChange={(v) => update({ origin_countries: v })}
            placeholder="US, CA"
          />
          <CSVInput
            label="Destination airport codes"
            value={draft.destination_airports}
            onChange={(v) => update({ destination_airports: v })}
            placeholder="LHR, CDG, DXB"
            testid="rule-destination-airports"
          />
          <CSVInput
            label="Destination cities"
            value={draft.destination_cities}
            onChange={(v) => update({ destination_cities: v })}
            placeholder="London, Paris"
          />
          <CSVInput
            label="Destination country codes"
            value={draft.destination_countries}
            onChange={(v) => update({ destination_countries: v })}
            placeholder="GB, FR, IN"
          />
          <CSVInput
            label="Airline codes (IATA)"
            value={draft.airlines}
            onChange={(v) => update({ airlines: v })}
            placeholder="EK, QR, BA"
          />
          <div>
            <label className="tcp-overline text-[#666460] block mb-1.5">Trip direction</label>
            <select
              value={draft.trip_direction}
              onChange={(e) => update({ trip_direction: e.target.value })}
              className="tcp-input"
            >
              {TRIP_DIRECTIONS.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
          </div>

          <div className="md:col-span-2">
            <ChipsInput
              label="Cabin classes"
              options={CABINS}
              value={draft.cabins}
              onChange={(v) => update({ cabins: v })}
              testid="rule-cabins"
            />
          </div>
          <div className="md:col-span-2">
            <ChipsInput
              label="Passenger types"
              options={PASSENGER_TYPES}
              value={draft.passenger_types}
              onChange={(v) => update({ passenger_types: v })}
              testid="rule-pax-types"
            />
          </div>

          {/* --- Dates --- */}
          <div className="md:col-span-2 mt-3">
            <h4 className="font-serif text-xl text-[#1C1C1C] mb-3 pb-1 border-b border-[#E0DDD6]">Date windows</h4>
          </div>
          <div>
            <label className="tcp-overline text-[#666460] block mb-1.5">Booking date — start</label>
            <input type="date" value={draft.booking_date_start} onChange={(e) => update({ booking_date_start: e.target.value })} className="tcp-input" />
          </div>
          <div>
            <label className="tcp-overline text-[#666460] block mb-1.5">Booking date — end</label>
            <input type="date" value={draft.booking_date_end} onChange={(e) => update({ booking_date_end: e.target.value })} className="tcp-input" />
          </div>
          <div>
            <label className="tcp-overline text-[#666460] block mb-1.5">Travel date — start</label>
            <input type="date" value={draft.travel_date_start} onChange={(e) => update({ travel_date_start: e.target.value })} className="tcp-input" />
          </div>
          <div>
            <label className="tcp-overline text-[#666460] block mb-1.5">Travel date — end</label>
            <input type="date" value={draft.travel_date_end} onChange={(e) => update({ travel_date_end: e.target.value })} className="tcp-input" />
          </div>

          {/* --- Guardrails --- */}
          <div className="md:col-span-2 mt-3">
            <h4 className="font-serif text-xl text-[#1C1C1C] mb-3 pb-1 border-b border-[#E0DDD6]">Safety guardrails</h4>
          </div>
          <div>
            <label className="tcp-overline text-[#666460] block mb-1.5">Min delta ($)</label>
            <input type="number" step="0.01" value={draft.min_amount} onChange={(e) => update({ min_amount: e.target.value })} className="tcp-input" placeholder="e.g. 10" />
          </div>
          <div>
            <label className="tcp-overline text-[#666460] block mb-1.5">Max delta ($)</label>
            <input type="number" step="0.01" value={draft.max_amount} onChange={(e) => update({ max_amount: e.target.value })} className="tcp-input" placeholder="e.g. 250" />
          </div>
          <div>
            <label className="tcp-overline text-[#666460] block mb-1.5">Max delta (%)</label>
            <input type="number" step="0.01" value={draft.max_percent} onChange={(e) => update({ max_percent: e.target.value })} className="tcp-input" placeholder="e.g. 15" />
          </div>
          <div className="flex items-center gap-3 pt-6">
            <Switch
              checked={!!draft.allow_below_cost}
              onCheckedChange={(v) => update({ allow_below_cost: v })}
              className="data-[state=checked]:bg-[#C29B62]"
              data-testid="rule-allow-below-cost"
            />
            <span className="text-sm text-[#1C1C1C]">Allow final fare below supplier base cost</span>
          </div>

          <div className="md:col-span-2 flex items-center gap-3 pt-4 border-t border-[#E0DDD6]">
            <Switch
              checked={!!draft.active}
              onCheckedChange={(v) => update({ active: v })}
              className="data-[state=checked]:bg-[#C29B62]"
              data-testid="rule-active"
            />
            <span className="text-sm text-[#1C1C1C]">Rule is active</span>
          </div>
        </div>

        <DialogFooter className="mt-6">
          <button onClick={onClose} className="px-5 py-2.5 text-xs tracking-[0.16em] uppercase border border-[#E0DDD6] text-[#666460] hover:border-[#1C1C1C] hover:text-[#1C1C1C]">Cancel</button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="rule-save"
            className="btn-gold text-xs tracking-[0.16em] uppercase"
          >
            {saving ? "Saving..." : initial?.id ? "Save changes" : "Create rule"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Simulator() {
  const [form, setForm] = useState({
    base_amount: 1000,
    passenger_count: 1,
    origin_airport: "JFK", origin_city: "", origin_country: "",
    destination_airport: "LHR", destination_city: "", destination_country: "",
    airline_code: "", cabin: "economy", trip_type: "round_trip",
    booking_date: "", travel_date: "",
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const r = await api.post("/admin/pricing/simulate", {
        base_amount: Number(form.base_amount) || 0,
        passenger_count: Number(form.passenger_count) || 1,
        context: {
          origin_airport: form.origin_airport.toUpperCase() || null,
          origin_city: form.origin_city || null,
          origin_country: form.origin_country.toUpperCase() || null,
          destination_airport: form.destination_airport.toUpperCase() || null,
          destination_city: form.destination_city || null,
          destination_country: form.destination_country.toUpperCase() || null,
          airline_code: form.airline_code.toUpperCase() || null,
          cabin: form.cabin || null,
          trip_type: form.trip_type || null,
          booking_date: form.booking_date || null,
          travel_date: form.travel_date || null,
        },
      });
      setResult(r.data);
    } catch (e) {
      toast.error("Simulation failed");
    } finally {
      setLoading(false);
    }
  };

  const u = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="bg-white border border-[#E0DDD6] p-6" data-testid="pricing-simulator">
      <div className="flex items-center gap-3 mb-5">
        <Calculator size={22} className="text-[#C29B62]" weight="duotone" />
        <h3 className="font-serif text-2xl tracking-tighter text-[#1C1C1C]">Pricing simulator</h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <div>
          <label className="tcp-overline text-[#666460] block mb-1">Base fare ($)</label>
          <input type="number" step="0.01" value={form.base_amount} onChange={(e) => u("base_amount", e.target.value)} className="tcp-input" data-testid="sim-base" />
        </div>
        <div>
          <label className="tcp-overline text-[#666460] block mb-1">Passengers</label>
          <input type="number" min="1" value={form.passenger_count} onChange={(e) => u("passenger_count", e.target.value)} className="tcp-input" data-testid="sim-pax" />
        </div>
        <div>
          <label className="tcp-overline text-[#666460] block mb-1">Origin (IATA)</label>
          <input value={form.origin_airport} onChange={(e) => u("origin_airport", e.target.value)} className="tcp-input" data-testid="sim-origin" />
        </div>
        <div>
          <label className="tcp-overline text-[#666460] block mb-1">Destination (IATA)</label>
          <input value={form.destination_airport} onChange={(e) => u("destination_airport", e.target.value)} className="tcp-input" data-testid="sim-destination" />
        </div>
        <div>
          <label className="tcp-overline text-[#666460] block mb-1">Cabin</label>
          <select value={form.cabin} onChange={(e) => u("cabin", e.target.value)} className="tcp-input">
            {CABINS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="tcp-overline text-[#666460] block mb-1">Airline (IATA)</label>
          <input value={form.airline_code} onChange={(e) => u("airline_code", e.target.value)} className="tcp-input" />
        </div>
        <div>
          <label className="tcp-overline text-[#666460] block mb-1">Trip type</label>
          <select value={form.trip_type} onChange={(e) => u("trip_type", e.target.value)} className="tcp-input">
            <option value="one_way">One-way</option>
            <option value="round_trip">Round-trip</option>
            <option value="multi_city">Multi-city</option>
          </select>
        </div>
        <div>
          <label className="tcp-overline text-[#666460] block mb-1">Travel date</label>
          <input type="date" value={form.travel_date} onChange={(e) => u("travel_date", e.target.value)} className="tcp-input" />
        </div>
      </div>
      <button onClick={run} disabled={loading} className="btn-gold text-xs tracking-[0.16em] uppercase" data-testid="sim-run">
        {loading ? "Calculating..." : "Run simulation"}
      </button>

      {result && (
        <div className="mt-6 border-t border-[#E0DDD6] pt-5" data-testid="sim-result">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div>
              <p className="tcp-overline text-[#666460]">Base fare</p>
              <p className="font-serif text-3xl tracking-tight text-[#1C1C1C]">{formatPrice(result.base_amount)}</p>
            </div>
            <div className="md:col-span-2">
              <p className="tcp-overline text-[#666460]">Final displayed fare</p>
              <p className="font-serif text-3xl tracking-tight text-[#C29B62]" data-testid="sim-final">{formatPrice(result.final_amount)}</p>
            </div>
            <div>
              <p className="tcp-overline text-[#666460]">Net margin</p>
              <p className="font-serif text-3xl tracking-tight text-[#1C1C1C]">{formatPrice(result.final_amount - result.base_amount)}</p>
            </div>
          </div>
          {result.components.length === 0 ? (
            <p className="text-sm text-[#666460] italic">No rules matched this context.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-[#666460]">
                <tr className="border-b border-[#E0DDD6]">
                  <th className="text-left py-2">Rule</th>
                  <th className="text-left py-2">Type</th>
                  <th className="text-left py-2">Method</th>
                  <th className="text-right py-2">Applied to</th>
                  <th className="text-right py-2">Delta</th>
                  <th className="text-left py-2">Note</th>
                </tr>
              </thead>
              <tbody>
                {result.components.map((c, i) => (
                  <tr key={`${c.rule_id ?? c.name}-${i}`} className="border-b border-[#E0DDD6]/50">
                    <td className="py-2 text-[#1C1C1C] font-medium">{c.name}</td>
                    <td className="py-2 text-xs uppercase tracking-wider">{c.rule_type}</td>
                    <td className="py-2 text-xs">{c.method} · {c.amount}{c.method === "percentage" ? "%" : ""}</td>
                    <td className="py-2 text-right font-mono text-xs">{formatPrice(c.applied_to)}</td>
                    <td className={`py-2 text-right font-mono ${c.delta < 0 ? "text-rose-600" : "text-emerald-700"}`}>
                      {c.delta < 0 ? "−" : "+"}{formatPrice(Math.abs(c.delta))}
                    </td>
                    <td className="py-2 text-xs text-[#666460]">{c.note || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

function AuditLog() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/admin/pricing/audit").then(({ data }) => setItems(data || [])).catch(() => {});
  }, []);

  return (
    <div className="bg-white border border-[#E0DDD6] p-6" data-testid="pricing-audit">
      <div className="flex items-center gap-3 mb-5">
        <ClockCounterClockwise size={22} className="text-[#C29B62]" weight="duotone" />
        <h3 className="font-serif text-2xl tracking-tighter text-[#1C1C1C]">Audit log</h3>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-[#666460]">No pricing changes yet.</p>
      ) : (
        <div className="space-y-3 max-h-[60vh] overflow-y-auto">
          {items.map((e) => (
            <div key={e.id} className="border border-[#E0DDD6] p-3 text-xs" data-testid={`audit-${e.id}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 uppercase tracking-wider text-[10px] ${
                    e.action === "create" ? "bg-emerald-50 text-emerald-700"
                    : e.action === "update" ? "bg-amber-50 text-amber-700"
                    : e.action === "delete" ? "bg-rose-50 text-rose-700"
                    : "bg-stone-100 text-stone-700"
                  }`}>{e.action}</span>
                  <span className="text-[#1C1C1C] font-medium">{e.after?.name || e.before?.name || e.rule_id}</span>
                </div>
                <span className="text-[#666460]">{new Date(e.created_at).toLocaleString()} · {e.actor_email}</span>
              </div>
              {e.before && e.after && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-[#666460] hover:text-[#C29B62]">View diff</summary>
                  <pre className="mt-2 p-2 bg-[#F7F5F0] overflow-x-auto text-[11px] leading-snug">
                    {JSON.stringify({ before: e.before, after: e.after }, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PricingModule() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [view, setView] = useState("rules"); // rules | simulator | audit
  const [filter, setFilter] = useState("all"); // all | active | inactive

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/pricing/rules");
      setRules(data || []);
    } catch (e) {
      toast.error("Could not load pricing rules");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleActive = async (rule) => {
    try {
      const { id, created_by, created_at, updated_by, updated_at, ...rest } = rule;
      await api.put(`/admin/pricing/rules/${rule.id}`, { ...rest, active: !rule.active });
      load();
    } catch {
      toast.error("Failed to toggle rule");
    }
  };

  const remove = async (rule) => {
    if (!confirm(`Delete pricing rule "${rule.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/pricing/rules/${rule.id}`);
      toast.success("Rule deleted");
      load();
    } catch {
      toast.error("Failed to delete rule");
    }
  };

  const move = async (rule, dir) => {
    const sorted = [...rules].sort((a, b) => a.priority - b.priority);
    const idx = sorted.findIndex((r) => r.id === rule.id);
    const swapIdx = dir === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const a = sorted[idx], b = sorted[swapIdx];
    const items = [
      { id: a.id, priority: b.priority },
      { id: b.id, priority: a.priority },
    ];
    try {
      await api.post("/admin/pricing/rules/reorder", { items });
      load();
    } catch {
      toast.error("Failed to reorder");
    }
  };

  const visible = rules
    .filter((r) => filter === "all" || (filter === "active" ? r.active : !r.active))
    .sort((a, b) => a.priority - b.priority);

  return (
    <div className="space-y-5" data-testid="pricing-module">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setView("rules")}
            data-testid="pricing-view-rules"
            className={`px-4 py-2 text-xs tracking-[0.16em] uppercase border ${
              view === "rules" ? "bg-[#C29B62] text-[#0A0A0B] border-[#C29B62]" : "border-[#E0DDD6] text-[#666460] hover:border-[#C29B62] hover:text-[#C29B62]"
            }`}
          >
            <Funnel size={14} weight="duotone" className="inline mr-1.5" />
            Rules ({rules.length})
          </button>
          <button
            onClick={() => setView("simulator")}
            data-testid="pricing-view-simulator"
            className={`px-4 py-2 text-xs tracking-[0.16em] uppercase border ${
              view === "simulator" ? "bg-[#C29B62] text-[#0A0A0B] border-[#C29B62]" : "border-[#E0DDD6] text-[#666460] hover:border-[#C29B62] hover:text-[#C29B62]"
            }`}
          >
            <Calculator size={14} weight="duotone" className="inline mr-1.5" />
            Simulator
          </button>
          <button
            onClick={() => setView("audit")}
            data-testid="pricing-view-audit"
            className={`px-4 py-2 text-xs tracking-[0.16em] uppercase border ${
              view === "audit" ? "bg-[#C29B62] text-[#0A0A0B] border-[#C29B62]" : "border-[#E0DDD6] text-[#666460] hover:border-[#C29B62] hover:text-[#C29B62]"
            }`}
          >
            <ClockCounterClockwise size={14} weight="duotone" className="inline mr-1.5" />
            Audit log
          </button>
        </div>
        {view === "rules" && (
          <div className="flex items-center gap-2">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="tcp-input w-auto"
              data-testid="pricing-filter"
            >
              <option value="all">All rules</option>
              <option value="active">Active only</option>
              <option value="inactive">Inactive only</option>
            </select>
            <button
              onClick={() => load()}
              className="px-3 py-2 border border-[#E0DDD6] hover:border-[#1C1C1C] text-[#666460] hover:text-[#1C1C1C]"
              aria-label="Refresh"
              data-testid="pricing-refresh"
            >
              <ArrowsClockwise size={16} />
            </button>
            <button
              onClick={() => { setEditing(null); setEditorOpen(true); }}
              data-testid="pricing-new-rule"
              className="btn-gold text-xs tracking-[0.16em] uppercase"
            >
              <Plus size={14} weight="bold" /> New rule
            </button>
          </div>
        )}
      </div>

      {view === "rules" && (
        <div className="bg-white border border-[#E0DDD6] overflow-hidden" data-testid="rules-table">
          {loading ? (
            <p className="p-6 text-sm text-[#666460]">Loading rules...</p>
          ) : visible.length === 0 ? (
            <div className="p-10 text-center">
              <p className="text-sm text-[#666460] mb-3">No pricing rules yet — the website displays raw supplier fares.</p>
              <button onClick={() => { setEditing(null); setEditorOpen(true); }} className="btn-gold text-xs tracking-[0.16em] uppercase">
                Create your first rule
              </button>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#F7F5F0] text-[#666460]">
                <tr>
                  <th className="text-left py-3 px-4 w-16">Priority</th>
                  <th className="text-left py-3 px-4">Name</th>
                  <th className="text-left py-3 px-4">Effect</th>
                  <th className="text-left py-3 px-4">Scope</th>
                  <th className="text-left py-3 px-4">Combine</th>
                  <th className="text-center py-3 px-4 w-20">Active</th>
                  <th className="text-right py-3 px-4 w-32">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => {
                  const sign = r.rule_type === "markdown" ? "−" : "+";
                  const method = r.method === "percentage" ? `${r.amount}%` : `$${r.amount} ${r.method === "fixed_per_pax" ? "/ pax" : "/ booking"}`;
                  const scope = [
                    r.origin_airports?.length || r.origin_cities?.length || r.origin_countries?.length
                      ? `from ${[...(r.origin_airports || []), ...(r.origin_cities || []), ...(r.origin_countries || [])].join("/")}`
                      : null,
                    r.destination_airports?.length || r.destination_cities?.length || r.destination_countries?.length
                      ? `to ${[...(r.destination_airports || []), ...(r.destination_cities || []), ...(r.destination_countries || [])].join("/")}`
                      : null,
                    r.airlines?.length ? `airlines ${r.airlines.join(",")}` : null,
                    r.cabins?.length ? `cabins ${r.cabins.join(",")}` : null,
                  ].filter(Boolean).join(" · ") || "All flights (global)";
                  return (
                    <tr key={r.id} className="border-t border-[#E0DDD6]" data-testid={`rule-row-${r.id}`}>
                      <td className="py-3 px-4 font-mono text-xs">
                        <div className="flex items-center gap-1">
                          <span>{r.priority}</span>
                          <div className="flex flex-col">
                            <button onClick={() => move(r, "up")} className="text-[#666460] hover:text-[#C29B62]" aria-label="Move up" data-testid={`rule-up-${r.id}`}><CaretUp size={11} weight="bold" /></button>
                            <button onClick={() => move(r, "down")} className="text-[#666460] hover:text-[#C29B62]" aria-label="Move down" data-testid={`rule-down-${r.id}`}><CaretDown size={11} weight="bold" /></button>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-[#1C1C1C] font-medium">{r.name}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider ${r.rule_type === "markdown" ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"}`}>{sign} {method}</span>
                      </td>
                      <td className="py-3 px-4 text-xs text-[#666460]">{scope}</td>
                      <td className="py-3 px-4 text-xs">
                        {r.combine_mode === "combine" && <span className="text-[#666460]">Combine</span>}
                        {r.combine_mode === "stop_after" && <span className="text-amber-700">Stop after</span>}
                        {r.combine_mode === "solo" && <span className="text-rose-700">Solo only</span>}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <button onClick={() => toggleActive(r)} aria-label="Toggle active" data-testid={`rule-toggle-${r.id}`}>
                          {r.active
                            ? <CheckCircle size={20} weight="fill" className="text-emerald-600" />
                            : <XCircle size={20} weight="duotone" className="text-stone-400" />}
                        </button>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <button onClick={() => { setEditing(r); setEditorOpen(true); }} aria-label="Edit" data-testid={`rule-edit-${r.id}`} className="text-[#666460] hover:text-[#C29B62] mr-3">
                          <PencilSimple size={16} />
                        </button>
                        <button onClick={() => remove(r)} aria-label="Delete" data-testid={`rule-delete-${r.id}`} className="text-[#666460] hover:text-rose-600">
                          <Trash size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          <div className="p-3 border-t border-[#E0DDD6] bg-[#F7F5F0] flex items-center gap-2 text-xs text-[#666460]">
            <WarningCircle size={14} className="text-[#C29B62]" />
            Rules are evaluated in priority order (lower first). Final fare is silently floored at supplier base cost unless a rule explicitly allows below-cost.
          </div>
        </div>
      )}

      {view === "simulator" && <Simulator />}
      {view === "audit" && <AuditLog />}

      <RuleEditor
        open={editorOpen}
        initial={editing}
        onClose={() => setEditorOpen(false)}
        onSaved={load}
      />
    </div>
  );
}
